import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CloudUpload } from '@styled-icons/bootstrap/CloudUpload';
import { FileEarmarkText } from '@styled-icons/bootstrap/FileEarmarkText';
import React, { FC } from 'react';
import { Accept, DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import AllowedFileType from 'Components/AllowedFileType';

export enum ESupportedFileTypes {
  PDF = 'pdf',
  CSV = 'csv',
  XLS = 'xls',
  XLSX = 'xlsx',
  KML = 'kml',
  XML = 'xml',
  JSON = 'json',
  GEO_JSON = 'geojson',
  IMAGE = 'image',
  VIDEO = 'video',
  TXT = 'txt',
}

export interface IDropzoneProps {
  maxFileMb?: number;
  multiple?: boolean;
  disabled?: boolean;
  maxFiles?: number;
  label?: string;
  supportedFileTypes?: `${ESupportedFileTypes}`[];
  onDrop: (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => void;
}

export const FILE_MIME_TYPES: Record<ESupportedFileTypes, Accept> = {
  [ESupportedFileTypes.PDF]: { 'application/pdf': ['.pdf'] },
  [ESupportedFileTypes.CSV]: { 'text/csv': ['.csv'] },
  [ESupportedFileTypes.XLS]: { 'application/vnd.ms-excel': ['.xls'] },
  [ESupportedFileTypes.XLSX]: {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  },
  [ESupportedFileTypes.KML]: { 'application/vnd.google-earth.kml+xml': ['.kml'] },
  [ESupportedFileTypes.XML]: { 'application/xml': ['.xml'] },
  [ESupportedFileTypes.JSON]: { 'application/json': ['.json'] },
  [ESupportedFileTypes.GEO_JSON]: { 'application/geo+json': ['.geojson'] },
  [ESupportedFileTypes.IMAGE]: {
    'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.gif', '.webp', '.heic'],
  },
  [ESupportedFileTypes.VIDEO]: { 'video/*': ['.mp4', '.mpeg', '.ogv', '.mov'] },
  [ESupportedFileTypes.TXT]: { 'text/plain': ['.txt'] },
};

export const IMAGE_FILE_TYPES = Object.values(FILE_MIME_TYPES[ESupportedFileTypes.IMAGE]).flat();
export const VIDEO_FILE_TYPES = Object.values(FILE_MIME_TYPES[ESupportedFileTypes.VIDEO]).flat();

export const DropContainer = styled('div')<{ disabled: Tbooleanish }>(({ theme, disabled }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  color: theme.palette.grey[500],
  marginBottom: theme.spacing(4),
  backgroundColor: '#fff',
  marginTop: theme.spacing(2),

  '& .dropzone': {
    flex: 1,
    display: 'flex',
    height: '130px',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
  },

  '&:hover': {
    cursor: disabled === 'true' ? 'default' : 'pointer',
  },
}));

const IconContainer = styled('div')(({ theme }) => ({
  color: theme.palette.grey[500],
  marginBottom: theme.spacing(2),
}));

const UploadContainer = styled('div')<{ 'is-drag-active': Tbooleanish; disabled: Tbooleanish }>(
  ({ theme, 'is-drag-active': isDragActive, disabled }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    borderWidth: 2,
    borderColor: theme.custom.themeColors.grayScale[30],
    borderStyle: 'dashed',
    textAlign: 'center',
    transition: 'all 0.125s ease',
    height: '250px',

    '& *': {
      color: theme.palette.grey[500],
      fill: theme.palette.grey[500],
    },

    ...(disabled === 'false'
      ? {
          cursor: 'pointer',
          '&:hover': {
            borderColor: theme.custom.colors.blueFrost,

            '& *': {
              color: theme.custom.colors.blueFrost,
              fill: theme.custom.colors.blueFrost,
            },
          },
        }
      : {}),
  })
);

const Description = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const Dropzone: FC<IDropzoneProps> = ({
  maxFileMb = 2,
  supportedFileTypes = ['pdf'],
  multiple = true,
  disabled = false,
  maxFiles = 20,
  label = `Click or drag and drop to upload ${multiple ? `up to ${maxFiles} files` : 'a files'}`,
  onDrop,
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled,
    onDrop,
    multiple,
    maxFiles,
    accept: supportedFileTypes.reduce((prev, type) => ({ ...prev, ...FILE_MIME_TYPES[type] }), {}),
  });

  return (
    <DropContainer className="dropzone-container" disabled={Booleanish(disabled)}>
      <div
        {...getRootProps({
          className: 'dropzone',
        })}
      >
        <input {...getInputProps()} data-testid="dropzone-input" />
        <UploadContainer is-drag-active={Booleanish(isDragActive)} disabled={Booleanish(disabled)}>
          <IconContainer>
            <CloudUpload size={40} />
          </IconContainer>
          <Description color="inherit" variant="subtitle2">
            {label}
          </Description>
          <AllowedFileType
            icon={<FileEarmarkText size={20} />}
            description={`${supportedFileTypes.reduce(
              (prev, type) =>
                `${prev} ${Object.values(FILE_MIME_TYPES[type]).reduce(
                  (prev, next) => `${prev} ${next}`,
                  ''
                )}`,
              ''
            )}
             up to ${maxFileMb}MB`}
            title={undefined}
          />
        </UploadContainer>
      </div>
    </DropContainer>
  );
};

export default Dropzone;
